import { useCallback, useEffect, useRef } from 'react';
import { initialFormData } from '../types';
import type { CampaignFormData } from '../types';

const DRAFT_KEY = 'hiba_campaign_draft';
const DRAFT_STEP_KEY = 'hiba_campaign_draft_step';
const AUTO_SAVE_DELAY = 1500; // ms

export interface DraftMeta {
  savedAt: string;       // ISO string
  step: number;
  hasContent: boolean;
}

export interface DraftData {
  formData: CampaignFormData;
  step: number;
  savedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
export const loadDraft = (): DraftData | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    const stepRaw = localStorage.getItem(DRAFT_STEP_KEY);
    if (!raw) return null;
    const formData = JSON.parse(raw) as CampaignFormData;
    const step = stepRaw ? Number(stepRaw) : 1;
    const savedAt = localStorage.getItem(`${DRAFT_KEY}_time`) ?? new Date().toISOString();
    // Check if draft has any meaningful content
    const hasContent = formData.title.trim().length > 0 || formData.story.trim().length > 0;
    if (!hasContent) return null;
    return { formData, step, savedAt };
  } catch {
    return null;
  }
};

export const clearDraft = () => {
  localStorage.removeItem(DRAFT_KEY);
  localStorage.removeItem(DRAFT_STEP_KEY);
  localStorage.removeItem(`${DRAFT_KEY}_time`);
};

const saveDraftToStorage = (formData: CampaignFormData, step: number) => {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    localStorage.setItem(DRAFT_STEP_KEY, String(step));
    localStorage.setItem(`${DRAFT_KEY}_time`, new Date().toISOString());
  } catch {
    // Storage might be full — silently ignore
  }
};

export const formatDraftTime = (isoString: string): string => {
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffMin < 1) return 'الآن';
    if (diffMin < 60) return `منذ ${diffMin} دقيقة`;
    if (diffHr < 24) return `منذ ${diffHr} ساعة`;
    return `منذ ${diffDay} يوم`;
  } catch {
    return '';
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
interface UseCampaignDraftOptions {
  formData: CampaignFormData;
  currentStep: number;
  onSaved?: () => void;
}

const useCampaignDraft = ({ formData, currentStep, onSaved }: UseCampaignDraftOptions) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);

  // Auto-save whenever formData or step changes (debounced)
  useEffect(() => {
    // Skip very first render to avoid overwriting a just-restored draft
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const hasContent =
      formData.title.trim().length > 0 ||
      formData.story.trim().length > 0 ||
      formData.goalAmount.trim().length > 0;

    if (!hasContent) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      saveDraftToStorage(formData, currentStep);
      onSaved?.();
    }, AUTO_SAVE_DELAY);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [formData, currentStep, onSaved]);

  const saveDraftNow = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    saveDraftToStorage(formData, currentStep);
    onSaved?.();
  }, [formData, currentStep, onSaved]);

  return { saveDraftNow };
};

export default useCampaignDraft;

// Re-export initialFormData for convenience
export { initialFormData };
